import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, X } from 'lucide-react';
import { Button } from '../ui/Button';
import { useEscapeKey } from '../../app/hooks/useEscapeKey';
import { useFocusTrap } from '../../app/hooks/useFocusTrap';
import { soundManager } from '../../utils/soundManager';
import { hapticManager } from '../../utils/hapticManager';
interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'info';
  onConfirm: () => void;
  onCancel: () => void;
  /**
   * Extra inhoud tussen de tekst en de knoppen — bedoeld voor een
   * bevestigingsveld bij écht onherstelbare acties, waarbij de gebruiker de
   * naam van wat hij verwijdert moet overtikken. Dat is geen ritueel: het
   * dwingt hem te lezen wélk item hij weggooit.
   */
  children?: React.ReactNode;
  /** Houdt de bevestigknop uit tot aan een voorwaarde is voldaan. */
  confirmDisabled?: boolean;
}
const VARIANT_STYLES = {
  danger: {
    iconBg: 'bg-rose-100',
    iconColor: 'text-rose-600',
    confirmClass: 'bg-rose-600 hover:bg-rose-700 text-white'
  },
  warning: {
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    confirmClass: 'bg-amber-600 hover:bg-amber-700 text-white'
  },
  info: {
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    confirmClass: 'bg-blue-600 hover:bg-blue-700 text-white'
  }
};
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Bevestigen',
  cancelLabel = 'Annuleren',
  variant = 'danger',
  onConfirm,
  onCancel,
  children,
  confirmDisabled = false
}: ConfirmDialogProps) {
  const styles = VARIANT_STYLES[variant];
  // Close on Escape
  useEscapeKey(onCancel, open);
  // Zonder focusvang staat de focus na het openen nog op de knop erachter.
  // Iemand die met het toetsenbord werkt, tabt dan langs de pagina eronder in
  // plaats van langs "Annuleren" en "Verwijderen" — en bevestigt in het
  // ergste geval iets wat hij niet kan zien.
  const dialogRef = useFocusTrap(open);
  // F-FEEL-1 + F-FEEL-2: signal a destructive decision moment when the
  // danger variant opens — silent for info/warning so we don't over-fire.
  useEffect(() => {
    if (open && variant === 'danger') {
      soundManager.playError();
      hapticManager.hapticError();
    }
  }, [open, variant]);
  return <AnimatePresence data-id="element-2469" data-mp="dzsg66uxjrkw">
      {open && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15
      }} className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50" onClick={onCancel} data-id="element-2470" data-mp="iz5d5kfimf17" />
        

          {/* Dialog */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} transition={{
        duration: 0.2,
        ease: 'easeOut'
      }} ref={dialogRef} className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="confirm-dialog-title" data-id="element-2471" data-mp="2ot56e0ey3ppp">
          
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 relative" data-id="element-2472" data-mp="27jaui8nna2cg">
              <button onClick={onCancel} className="absolute top-4 right-4 p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors" aria-label="Sluiten" data-id="element-2473" data-mp="3571ihi0wilut">
              
                <X className="w-4 h-4" data-id="element-2474" data-mp="2v8vi54uqtns2" />
              </button>

              <div className="flex items-start gap-4" data-id="element-2475" data-mp="2v7apxlvwigxd">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${styles.iconBg}`} data-id="element-2476" data-mp="2r7ti4lt057p1">
                
                  <AlertTriangle className={`w-6 h-6 ${styles.iconColor}`} data-id="element-2477" data-mp="1jv9slikz1k60" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-2478" data-mp="1fqjzg1hp6jos">
                  <h3 id="confirm-dialog-title" className="text-lg font-bold text-slate-900 mb-1" data-id="element-2479" data-mp="33p2v7pptsblk">
                  
                    {title}
                  </h3>
                  {/*
                   `whitespace-pre-line` is hier geen opmaakvoorkeur maar een
                   reparatie: meerdere bevestigingen schrijven hun gevolgen als
                   opsomming met regeleinden ("• 24 leerlingen blijven
                   gekoppeld\n• Koppelcode vervalt"). Zonder deze regel plakt
                   HTML dat tot één doorlopende zin aan elkaar, precies op het
                   moment dat iemand moet kunnen overzien wat hij weggooit.
                   */}
                  <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line" data-id="element-2480" data-mp="3dgyv96bh2bsf">
                    {message}
                  </p>
                </div>
              </div>

              {children && <div className="mt-4" data-id="element-2481" data-mp="221tsk2bccd8t">{children}</div>}

              <div className="flex gap-3 justify-end mt-6" data-id="element-2482" data-mp="2623tv1o6ufhc">
                <Button variant="secondary" onClick={onCancel} className="text-sm" data-id="element-2483" data-mp="1yhjm5o50a18k">
                
                  {cancelLabel}
                </Button>
                <button onClick={onConfirm} disabled={confirmDisabled} className={`px-4 py-2 rounded-xl text-sm font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${styles.confirmClass}`} data-id="element-2484" data-mp="1thx9bft3an0a">
                
                  {confirmLabel}
                </button>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}